import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { api, apiError, resolveAsset } from "@/lib/api";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Info, Phone, FileText, Award, Building2, CheckCircle2, ArrowRight, Sparkles, Trophy, Crown, FlaskConical } from "lucide-react";

function ScoreRing({ score }) {
  const r = 26, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#E2E8F0" strokeWidth="6" />
        <circle cx="32" cy="32" r={r} fill="none" stroke="url(#g)" strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
        <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1E40AF" /><stop offset="100%" stopColor="#38BDF8" /></linearGradient></defs>
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-extrabold text-slate-900">{score}%</span>
    </div>
  );
}

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const avatarColor = (name) => { const colors = ["bg-indigo-500", "bg-[#6E6FB2]", "bg-[#8E7FB0]", "bg-[#5A6490]", "bg-[#7E82C4]", "bg-[#4A4FA0]"]; let h = 0; for (const ch of name || "x") h += ch.charCodeAt(0); return colors[h % colors.length]; };

function InsurerLogo({ name, logo, website }) {
  const domain = website ? website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : "";
  const sources = [resolveAsset(logo), domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const done = idx >= sources.length;
  return (
    <div className="h-12 w-full flex items-center justify-center">
      {!done ? (
        <img src={sources[idx]} alt={name} onError={() => setIdx((i) => i + 1)}
          className="max-h-10 max-w-[130px] object-contain" />
      ) : (
        <div className={`w-11 h-11 rounded-xl ${avatarColor(name)} text-white flex items-center justify-center font-extrabold text-sm`}>{initials(name)}</div>
      )}
    </div>
  );
}

export default function Results() {
  const { prospect_id } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const results_token = location.state?.results_token || sessionStorage.getItem(`rt_${prospect_id}`) || new URLSearchParams(location.search).get("rt");

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!results_token) { setError("Accès non autorisé. Veuillez vérifier votre e-mail."); return; }
    api.get(`/results/${prospect_id}`, { params: { results_token } }).then((r) => setData(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
    // eslint-disable-next-line
  }, []);

  const request = async (kind) => {
    setSending(true);
    try {
      await api.post("/results/request", { prospect_id, results_token, kind, message: msg });
      toast.success(kind === "devis" ? "Votre demande de devis a été transmise à un conseiller." : "Un conseiller vous rappellera prochainement.");
      setDialog(null); setMsg("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  if (error) return (
    <PublicLayout><div className="max-w-md mx-auto py-24 text-center px-4">
      <ShieldAlert className="mx-auto text-rose-500 mb-4" size={40} />
      <h1 className="font-display text-xl font-bold text-slate-900">Accès protégé</h1>
      <p className="text-slate-600 mt-2">{error}</p>
      <Button className="mt-6 btn-gradient border-0 rounded-full" onClick={() => nav("/")}>Retour à l'accueil</Button>
    </div></PublicLayout>
  );
  if (!data) return <PublicLayout><div className="py-32 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" size={32} /></div></PublicLayout>;

  const needLabel = data.prospect.need_type === "mutuelle_france" ? "Mutuelle santé France" : "Assurance santé expatrié";
  const hasOffers = data.match?.has_offers;

  return (
    <PublicLayout>
      {/* header band */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(130deg,#0A192F,#13315c)" }}>
        <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl animate-blob" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 text-indigo-200 text-sm font-bold bg-indigo-500/10 border border-indigo-400/20 rounded-full px-4 py-1.5">
            <CheckCircle2 size={16} /> E-mail vérifié — comparatif débloqué
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-4">
            {data.prospect.first_name ? `${data.prospect.first_name}, voici votre comparatif` : "Votre comparatif personnalisé"}
          </h1>
          <p className="text-slate-300 mt-2 flex items-center gap-2"><Sparkles size={16} className="text-indigo-200" />{needLabel} · établi selon vos réponses</p>
        </div>
        <div className="relative h-8 bg-slate-50 rounded-t-[2rem]" />
      </div>

      <div className="bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 -mt-2" data-testid="comparator-results-grid">
          {/* disclaimer */}
          {data.demo ? (
            <Card className="p-5 bg-indigo-50 border-2 border-indigo-300 flex items-start gap-3 rounded-2xl" data-testid="demo-banner">
              <FlaskConical className="text-indigo-600 mt-0.5 shrink-0" size={22} />
              <div>
                <p className="text-sm font-bold text-indigo-900 uppercase tracking-wide">Tarifs de démonstration — non contractuels</p>
                <p className="text-sm text-indigo-800 mt-1">{data.disclaimer}</p>
              </div>
            </Card>
          ) : (
            <Card className="p-5 bg-indigo-50/70 border-indigo-200 flex items-start gap-3 rounded-2xl">
              <Info className="text-indigo-600 mt-0.5 shrink-0" size={20} />
              <p className="text-sm text-indigo-900">{data.disclaimer}</p>
            </Card>
          )}

          {hasOffers ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {data.match.offers.map((o) => {
                const isBest = o.id === data.match.best_match_id;
                const isCheap = o.id === data.match.cheapest_id && !isBest;
                return (
                  <Card key={o.id} className={`relative p-6 flex flex-col rounded-2xl transition-[transform,box-shadow] duration-200 hover:-translate-y-1.5 hover:shadow-xl ${isBest ? "border-2 border-indigo-400 shadow-lg shadow-indigo-500/10" : ""}`} data-testid="offer-card">
                    {o.is_demo && <div className="absolute top-3 right-3 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">DÉMO</div>}
                    {isBest && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow"><Crown size={13} /> Meilleur compromis</div>}
                    {isCheap && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8E7FB0] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow"><Trophy size={13} /> Tarif le plus bas</div>}
                    <div className="flex items-center gap-3 mt-2">
                      <div className={`w-11 h-11 rounded-xl ${avatarColor(o.insurer_name)} text-white flex items-center justify-center font-bold text-sm`}>{initials(o.insurer_name)}</div>
                      <div><p className="font-semibold text-slate-900 leading-tight">{o.insurer_name}</p><p className="font-display font-bold text-slate-900">{o.formula_name}</p></div>
                    </div>
                    <div className="flex items-end justify-between mt-5">
                      <p className="text-3xl font-extrabold text-blue-700">{o.monthly_price}€<span className="text-sm text-slate-400 font-medium">/mois{o.is_demo ? "*" : ""}</span></p>
                      <ScoreRing score={o.match_score} />
                    </div>
                    {o.match_reasons?.length > 0 && <ul className="mt-4 space-y-1.5 text-sm text-slate-600">{o.match_reasons.map((r, i) => <li key={i} className="flex gap-2"><CheckCircle2 size={15} className="text-indigo-600 mt-0.5 shrink-0" />{r}</li>)}</ul>}
                    <Button className="mt-6 btn-gradient border-0 rounded-full" onClick={() => setDialog({ kind: "devis" })} data-testid="devis-request-button">Demander un devis</Button>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="mt-8 p-8 sm:p-10 text-center rounded-3xl relative overflow-hidden" style={{ background: "linear-gradient(135deg,#ffffff,#EFF6FF 60%,#F0FDF4)" }}>
              <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-indigo-200/40 blur-2xl" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-400 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-indigo-500/25"><Building2 className="text-white" size={30} /></div>
                <h2 className="font-display text-2xl font-extrabold text-slate-900">Votre étude personnalisée vous attend</h2>
                <p className="text-slate-600 mt-3 max-w-xl mx-auto leading-relaxed">
                  Pour vous éviter tout tarif erroné, un conseiller étudie votre profil et vous transmet des offres <strong>réelles et vérifiées</strong>. C'est gratuit et sans engagement.
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-7">
                  <Button className="btn-gradient border-0 rounded-full h-12 px-7" onClick={() => setDialog({ kind: "devis" })} data-testid="devis-request-button"><FileText size={17} className="mr-2" />Demander un devis personnalisé</Button>
                  <Button variant="outline" className="rounded-full h-12 px-7 bg-white" onClick={() => setDialog({ kind: "rappel" })} data-testid="callback-request-button"><Phone size={17} className="mr-2" />Être rappelé gratuitement</Button>
                </div>
              </div>
            </Card>
          )}

          {data.demo && hasOffers && (
            <p className="text-xs text-indigo-700 mt-4 italic" data-testid="demo-footnote">* Tarifs de démonstration à titre indicatif — non contractuels.</p>
          )}

          {/* potential insurers */}
          <div className="mt-14">
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Award size={20} className="text-blue-700" /> Assureurs étudiés pour votre profil</h2>
            <p className="text-sm text-slate-500 mt-1">Référentiel de partenaires potentiels. La disponibilité d'une offre dépend de la validation du conseiller.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
              {data.potential_insurers.map((ins) => (
                <Card key={ins.id} className="p-4 rounded-xl hover:shadow-md transition-shadow flex flex-col items-center gap-2 bg-white text-center">
                  <InsurerLogo name={ins.commercial_name} logo={ins.logo_url} website={ins.official_website} />
                  <span className="text-sm font-semibold text-slate-800 leading-tight">{ins.commercial_name}</span>
                  {ins.distribution_agreement_verified ? <Badge className="bg-indigo-600 text-xs">Validé</Badge> : <Badge variant="secondary" className="text-xs">À l'étude</Badge>}
                </Card>
              ))}
            </div>
          </div>

          <Card className="mt-12 p-7 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-4 text-white relative overflow-hidden" style={{ background: "linear-gradient(120deg,#1C1F45,#2E3370)" }}>            <div className="absolute -right-8 top-0 w-56 h-56 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="relative"><h3 className="font-display font-bold text-xl">Besoin d'aide pour choisir ?</h3><p className="text-sm text-slate-300 mt-1">Un conseiller vous accompagne gratuitement et sans engagement.</p></div>
            <div className="relative flex gap-3">
              <Button className="btn-gradient border-0 rounded-full" onClick={() => setDialog({ kind: "devis" })}>Demander un devis <ArrowRight size={16} className="ml-1" /></Button>
              <Button variant="outline" className="rounded-full bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={() => setDialog({ kind: "rappel" })}>Être rappelé</Button>
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{dialog?.kind === "devis" ? "Demander un devis personnalisé" : "Être rappelé par un conseiller"}</DialogTitle>
            <DialogDescription>Un conseiller traite votre demande et revient vers vous avec des offres réelles et vérifiées.</DialogDescription>
          </DialogHeader>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Précisez votre demande (facultatif)" data-testid="request-message" />
          <Button onClick={() => request(dialog.kind)} disabled={sending} className="btn-gradient border-0 rounded-full" data-testid="confirm-request">
            {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Envoyer ma demande
          </Button>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
